import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Star, MessageCircle, ThumbsUp, ThumbsDown, Eye, EyeOff, Send, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export default function ReviewsPage() {
  const { API, authHeaders, activeBranch } = useAuth();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const l = (ar, en) => isAr ? ar : en;

  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, average: 0, stars_distribution: [0, 0, 0, 0, 0] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [replyDialog, setReplyDialog] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const fetchReviews = useCallback(async () => {
    if (!activeBranch?.id) return;
    try {
      const status = tab === 'all' ? '' : tab;
      const { data } = await axios.get(`${API}/reviews?branch_id=${activeBranch.id}&status=${status}`, authHeaders());
      setReviews(data.reviews || []);
      setStats({ total: data.total, average: data.average, stars_distribution: data.stars_distribution });
    } catch { /* silent */ }
    setLoading(false);
  }, [API, authHeaders, activeBranch, tab]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleReply = async () => {
    if (!replyText.trim() || !replyDialog) return;
    setReplying(true);
    try {
      await axios.put(`${API}/reviews/${replyDialog.id}/reply`, { reply: replyText }, authHeaders());
      toast.success(l('تم إرسال الرد', 'Reply sent'));
      setReplyDialog(null);
      setReplyText('');
      fetchReviews();
    } catch { toast.error(l('حدث خطأ', 'Error')); }
    setReplying(false);
  };

  const updateStatus = async (reviewId, status) => {
    try {
      await axios.put(`${API}/reviews/${reviewId}/status?status=${status}`, {}, authHeaders());
      toast.success(l('تم التحديث', 'Updated'));
      fetchReviews();
    } catch { toast.error(l('حدث خطأ', 'Error')); }
  };

  const renderStars = (count, size = 'w-4 h-4') => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} className={size} fill={s <= count ? '#FBBF24' : 'none'} stroke={s <= count ? '#FBBF24' : '#D1D5DB'} />
      ))}
    </div>
  );

  const statusBadge = (status) => {
    const map = {
      pending: { label: l('قيد المراجعة', 'Pending'), variant: 'outline' },
      approved: { label: l('معتمد', 'Approved'), variant: 'default' },
      replied: { label: l('تم الرد', 'Replied'), variant: 'secondary' },
      hidden: { label: l('مخفي', 'Hidden'), variant: 'destructive' },
    };
    const s = map[status] || map.pending;
    return <Badge variant={s.variant} className="text-[10px]">{s.label}</Badge>;
  };

  return (
    <ModuleLayout>
      <div className="space-y-6" data-testid="reviews-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Cairo, sans-serif' }}>
            <Star className="w-6 h-6 inline-block me-2 text-amber-500" />
            {l('تقييمات العملاء', 'Customer Reviews')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{l('إدارة تقييمات ومراجعات عملائك', 'Manage your customer reviews and ratings')}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card data-testid="reviews-total-card">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-foreground">{stats.total}</p>
              <p className="text-xs text-muted-foreground">{l('إجمالي التقييمات', 'Total Reviews')}</p>
            </CardContent>
          </Card>
          <Card data-testid="reviews-avg-card">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-500">{stats.average}</p>
              <div className="flex justify-center mt-1">{renderStars(Math.round(stats.average), 'w-3.5 h-3.5')}</div>
            </CardContent>
          </Card>
          <Card data-testid="reviews-positive-card">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-500">
                {stats.total > 0 ? Math.round(((stats.stars_distribution[3] + stats.stars_distribution[4]) / stats.total) * 100) : 0}%
              </p>
              <p className="text-xs text-muted-foreground">{l('تقييم إيجابي', 'Positive')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="space-y-1.5">
                {[5, 4, 3, 2, 1].map(s => (
                  <div key={s} className="flex items-center gap-2">
                    <span className="text-[10px] w-3 text-muted-foreground">{s}</span>
                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-amber-400 transition-all" style={{ width: `${stats.total > 0 ? (stats.stars_distribution[s - 1] / stats.total) * 100 : 0}%` }} />
                    </div>
                    <span className="text-[10px] text-muted-foreground w-5 text-end">{stats.stars_distribution[s - 1]}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="reviews-tabs">
            <TabsTrigger value="all">{l('الكل', 'All')}</TabsTrigger>
            <TabsTrigger value="pending">{l('قيد المراجعة', 'Pending')}</TabsTrigger>
            <TabsTrigger value="approved">{l('معتمد', 'Approved')}</TabsTrigger>
            <TabsTrigger value="replied">{l('تم الرد', 'Replied')}</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : reviews.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Star className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">{l('لا توجد تقييمات', 'No reviews')}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {reviews.map(review => (
                  <Card key={review.id} data-testid={`review-card-${review.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-foreground">{review.customer_name}</span>
                            {renderStars(review.stars, 'w-3.5 h-3.5')}
                            {statusBadge(review.status)}
                          </div>
                          {review.item_name && (
                            <p className="text-xs text-primary mt-0.5">{l('صنف:', 'Item:')} {review.item_name}</p>
                          )}
                          {review.comment && <p className="text-sm text-muted-foreground mt-2">{review.comment}</p>}
                          {review.reply && (
                            <div className="mt-2 p-2.5 rounded-lg bg-primary/5 border border-primary/10">
                              <p className="text-xs font-semibold text-primary mb-0.5">{l('ردكم:', 'Your reply:')}</p>
                              <p className="text-xs text-foreground">{review.reply}</p>
                            </div>
                          )}
                          <p className="text-[10px] text-muted-foreground mt-2">
                            {new Date(review.created_at).toLocaleDateString(isAr ? 'ar-SA' : 'en-US', { dateStyle: 'medium' })}
                          </p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {!review.reply && (
                            <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" data-testid={`reply-btn-${review.id}`}
                              onClick={() => { setReplyDialog(review); setReplyText(''); }}>
                              <MessageCircle className="w-3 h-3" /> {l('رد', 'Reply')}
                            </Button>
                          )}
                          {review.status !== 'approved' && review.status !== 'replied' && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-500" onClick={() => updateStatus(review.id, 'approved')} data-testid={`approve-btn-${review.id}`}>
                              <ThumbsUp className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {review.status !== 'hidden' && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => updateStatus(review.id, 'hidden')} data-testid={`hide-btn-${review.id}`}>
                              <EyeOff className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Reply Dialog */}
        <Dialog open={!!replyDialog} onOpenChange={() => setReplyDialog(null)}>
          <DialogContent className="sm:max-w-md" data-testid="reply-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cairo, sans-serif' }}>{l('الرد على التقييم', 'Reply to Review')}</DialogTitle>
            </DialogHeader>
            {replyDialog && (
              <div className="space-y-3 mt-2">
                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">{replyDialog.customer_name}</span>
                    {renderStars(replyDialog.stars, 'w-3 h-3')}
                  </div>
                  {replyDialog.comment && <p className="text-sm text-muted-foreground">{replyDialog.comment}</p>}
                </div>
                <textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                  placeholder={l('اكتب ردك هنا...', 'Write your reply...')}
                  rows={3} className="w-full px-3 py-2 rounded-lg border text-sm resize-none" data-testid="reply-text-input" />
                <Button onClick={handleReply} disabled={replying || !replyText.trim()} className="w-full bg-amber-500 hover:bg-amber-600 text-white gap-2" data-testid="send-reply-btn">
                  <Send className="w-4 h-4" /> {replying ? '...' : l('إرسال الرد', 'Send Reply')}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </ModuleLayout>
  );
}
